// 기기 선택 관련 상태 관리 훅
import { useState, useCallback } from 'react';
import { DeviceModel, SubModel } from '../types';
import { DeviceCategory } from '../../../../constants/unifiedDeviceData';
import { DeviceConfiguration } from '../../../../constants/deviceConfigurations';

export const useDeviceSelection = () => {
  // 기본 선택 상태
  const [activeStep, setActiveStep] = useState(0);
  const [category, setCategory] = useState<DeviceCategory | ''>('');
  const [search, setSearch] = useState('');
  const [selectedModel, setSelectedModel] = useState<DeviceModel | null>(null);
  const [selectedSubModel, setSelectedSubModel] = useState<SubModel | null>(null);
  const [selectedConfiguration, setSelectedConfiguration] = useState<DeviceConfiguration | null>(null);
  const [customModel, setCustomModel] = useState('');
  const [customConfiguration, setCustomConfiguration] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('전체');

  // 제조사 선택
  const [selectedManufacturer, setSelectedManufacturer] = useState<'APPLE' | 'SAMSUNG' | ''>('');

  // iPhone 관련 상태
  const [selectedIphoneSeries, setSelectedIphoneSeries] = useState<string>('');
  const [selectedIphoneModel, setSelectedIphoneModel] = useState<string>('');
  const [selectedIphoneStorage, setSelectedIphoneStorage] = useState<string>('');
  const [customIphoneStorage, setCustomIphoneStorage] = useState<string>('');

  // Samsung 스마트폰 상태
  const [selectedSamsungSeries, setSelectedSamsungSeries] = useState<string>('');
  const [selectedSamsungModel, setSelectedSamsungModel] = useState<string>('');
  const [selectedSamsungStorage, setSelectedSamsungStorage] = useState<string>('');
  const [customSamsungStorage, setCustomSamsungStorage] = useState<string>('');

  // Samsung 태블릿 상태
  const [selectedSamsungTabletModel, setSelectedSamsungTabletModel] = useState<string>('');
  const [selectedSamsungTabletStorage, setSelectedSamsungTabletStorage] = useState<string>('');
  const [customSamsungTabletStorage, setCustomSamsungTabletStorage] = useState<string>('');
  const [selectedSamsungTabletConnectivity, setSelectedSamsungTabletConnectivity] = useState<'Wi-Fi' | 'Wi-Fi + Cellular' | ''>('');

  // Apple 태블릿 상태
  const [selectedAppleTabletConnectivity, setSelectedAppleTabletConnectivity] = useState<'Wi-Fi' | 'Wi-Fi + Cellular' | ''>('');
  const [selectedAppleTabletStorage, setSelectedAppleTabletStorage] = useState<string>('');
  const [customAppleTabletStorage, setCustomAppleTabletStorage] = useState<string>('');

  // 동적 스토리지 옵션
  const [customSamsungTabletStorages, setCustomSamsungTabletStorages] = useState<string[]>([]);
  const [customAppleTabletStorages, setCustomAppleTabletStorages] = useState<string[]>([]);

  // 스마트폰 선택값 초기화
  const resetSmartphoneSelections = useCallback(() => {
    setSelectedManufacturer('');
    setSelectedIphoneSeries('');
    setSelectedIphoneModel('');
    setSelectedIphoneStorage('');
    setCustomIphoneStorage('');
    setSelectedSamsungSeries('');
    setSelectedSamsungModel('');
    setSelectedSamsungStorage('');
    setCustomSamsungStorage('');
  }, []);

  // 태블릿 선택값 초기화
  const resetTabletSelections = useCallback(() => {
    // 공통 모델/구성
    setSelectedModel(null);
    setSelectedSubModel(null);
    setSelectedConfiguration(null);
    setCustomConfiguration('');
    // 태블릿 제조사별
    setSelectedSamsungTabletModel('');
    setSelectedSamsungTabletStorage('');
    setCustomSamsungTabletStorage('');
    setSelectedSamsungTabletConnectivity('');
    setCustomSamsungTabletStorages([]);
    setSelectedAppleTabletConnectivity('');
    setSelectedAppleTabletStorage('');
    setCustomAppleTabletStorages([]);
    setCustomAppleTabletStorage('');
  }, []);

  // 전체 초기화
  const resetAllSelections = useCallback(() => {
    setActiveStep(0);
    setCategory('');
    setSearch('');
    setSelectedModel(null);
    setSelectedSubModel(null);
    setSelectedConfiguration(null);
    setCustomModel('');
    setCustomConfiguration('');
    setSelectedSubcategory('전체');
    resetSmartphoneSelections();
    resetTabletSelections();
  }, [resetSmartphoneSelections, resetTabletSelections]);

  return {
    // 기본 상태
    activeStep, setActiveStep,
    category, setCategory,
    search, setSearch,
    selectedModel, setSelectedModel,
    selectedSubModel, setSelectedSubModel,
    selectedConfiguration, setSelectedConfiguration,
    customModel, setCustomModel,
    customConfiguration, setCustomConfiguration,
    selectedSubcategory, setSelectedSubcategory,
    
    // 제조사
    selectedManufacturer, setSelectedManufacturer,
    
    // iPhone 상태
    selectedIphoneSeries, setSelectedIphoneSeries,
    selectedIphoneModel, setSelectedIphoneModel,
    selectedIphoneStorage, setSelectedIphoneStorage,
    customIphoneStorage, setCustomIphoneStorage,
    
    // Samsung 스마트폰 상태
    selectedSamsungSeries, setSelectedSamsungSeries,
    selectedSamsungModel, setSelectedSamsungModel,
    selectedSamsungStorage, setSelectedSamsungStorage,
    customSamsungStorage, setCustomSamsungStorage,
    
    // Samsung 태블릿 상태
    selectedSamsungTabletModel, setSelectedSamsungTabletModel,
    selectedSamsungTabletStorage, setSelectedSamsungTabletStorage,
    customSamsungTabletStorage, setCustomSamsungTabletStorage,
    selectedSamsungTabletConnectivity, setSelectedSamsungTabletConnectivity,
    
    // Apple 태블릿 상태
    selectedAppleTabletConnectivity, setSelectedAppleTabletConnectivity,
    selectedAppleTabletStorage, setSelectedAppleTabletStorage,
    customAppleTabletStorage, setCustomAppleTabletStorage,
    
    // 동적 스토리지
    customSamsungTabletStorages, setCustomSamsungTabletStorages,
    customAppleTabletStorages, setCustomAppleTabletStorages,
    
    // 초기화 함수들
    resetSmartphoneSelections,
    resetTabletSelections,
    resetAllSelections
  };
};
